import type {
  CloseHandler,
  CloseReason
} from '@/pages/Administration/Devices/Devices.types';
import { IGNORED_REASONS } from '@/pages/Administration/Devices/Devices.types';

import {
  type SourceDetailResponse,
  SourceDialogMode
} from '../SourceCreate.types';
import type {
  UseDialogCleanupParams,
  UseDialogCleanupReturn
} from './SourceCreateDialog.types';

/**
 * Custom hook to manage dialog cleanup and close operations.
 *
 * Handles:
 * - Resetting all state when closing the dialog
 * - Special handling for AddThumbPrintId/AddAccessCode modes (refresh listing)
 * - Ignoring unwanted close reasons (backdrop clicks)
 *
 * @param params - State setters and callbacks
 * @returns Cleanup handlers
 */
export const useDialogCleanup = ({
  mode,
  reset,
  setSelectedSourceTypeId,
  setSelectedSourceDetails,
  setConnectionInputSchema,
  setIsSubmitting,
  setCurrentStep,
  setWizardSelectedSource,
  closeSourceDialog,
  refreshDeviceListing
}: UseDialogCleanupParams): UseDialogCleanupReturn => {
  const resetAndClose = () => {
    setSelectedSourceTypeId(0);
    setSelectedSourceDetails({} as SourceDetailResponse);
    setConnectionInputSchema({});
    setIsSubmitting(false);
    setCurrentStep(0);
    setWizardSelectedSource(null);
    closeSourceDialog();
    // reset form values
    reset();
  };

  const handleClose: CloseHandler = (_event: object, reason: CloseReason) => {
    if (IGNORED_REASONS.includes(reason)) {
      return;
    }

    if (
      mode === SourceDialogMode.AddThumbPrintId ||
      mode === SourceDialogMode.AddAccessCode
    ) {
      refreshDeviceListing();
    }

    resetAndClose();
  };

  return {
    resetAndClose,
    handleClose
  };
};
